import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import { collection, doc, getDocs, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase';
import ContactListStep from './SendEventSteps/ContactListStep';
import TripSelectionStep, { TripData } from './SendTripSteps/TripSelectionStep';
import EmailTemplateStep from './SendEventSteps/EmailTemplateStep';
import ReviewStep from './SendTripSteps/TripReviewStep';
import { formatDate } from '../../../../utils/dateUtils';

// Interface for selected contacts
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

// Interface for email template
interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeTripDetails: boolean;
}

// Define the Trip interface that matches EmailTemplateStep expectations
interface Trip {
  id?: string;
  tripName: string;
  departureDate: string;
  returnDate?: string;
  tripDescription?: string;
  destination?: string;
  transportationType?: string;
  accommodationDetails?: string;
  cost?: number;
  currency?: string;
  maxParticipants?: number;
  status?: 'planned' | 'booking' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled';
  imageUrl?: string;
}

// Props interface
interface SendTripDialogProps {
  open: boolean;
  onClose: () => void;
  contactsToInvite: string[];
  organizationId: string;
}

const SendTripDialog: React.FC<SendTripDialogProps> = ({
  open,
  onClose,
  contactsToInvite,
  organizationId,
}) => {
  // State management
  const [activeStep, setActiveStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [trips, setTrips] = useState<TripData[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<TripData | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [contactsData, setContactsData] = useState<Contact[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>({
    subject: '',
    body: '',
    includeCalendarInvite: true,
    includeTripDetails: true,
  });
  const [sendingStatus, setSendingStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [sendingProgress, setSendingProgress] = useState<number>(0);
  const [isSingleContact, setIsSingleContact] = useState<boolean>(false);

  // Steps for the stepper - dynamic based on whether it's a single contact or multiple
  const steps = isSingleContact 
    ? ['Select Trip', 'Customize Email', 'Review & Send'] 
    : ['Select Contacts', 'Select Trip', 'Customize Email', 'Review & Send'];

  // Fetch available trips on component mount
  useEffect(() => {
    if (open && organizationId) {
      fetchTrips();
      
      if (contactsToInvite.length > 0) {
        fetchSelectedContacts();
        
        // If only one contact is preselected, we're in single-contact mode
        if (contactsToInvite.length === 1) {
          setIsSingleContact(true);
          // Skip to trip selection for single contact
          setActiveStep(0);
        }
      }
    }
  }, [open, organizationId, contactsToInvite]);

  // Fetch trips from Firestore
  const fetchTrips = async () => {
    setLoading(true);
    try {
       // First, log the organizationId we're using to find trips

       // Try to fetch from trips collection
       const tripsRef = collection(db, 'trips');

       // Now try to get trips specifically for this organization
       const q = query(tripsRef, where('organizationId', '==', organizationId));
       const orgTripsSnapshot = await getDocs(q);

       // If no trips found, let's try an alternative field name
       if (orgTripsSnapshot.docs.length === 0) {
          const altQuery = query(tripsRef, where('orgId', '==', organizationId));
          const altOrgTripsSnapshot = await getDocs(altQuery);

          if (altOrgTripsSnapshot.docs.length > 0) {
             // Use data from altOrgTripsSnapshot
             const tripsData = processTripsData(altOrgTripsSnapshot.docs);
             setTrips(tripsData);
          } else {
             // Fallback to getting trips from events marked as trips
             await fetchTripsFromEvents();
          }
       } else {
          const tripsData = processTripsData(orgTripsSnapshot.docs);
          setTrips(tripsData);
       }
    } catch (error) {
      console.error('Error fetching trips:', error);
      
      // Fallback to getting trips from events marked as trips
      await fetchTripsFromEvents();
    } finally {
      setLoading(false);
    }
  };

  // Fallback: Try to find trips in events collection
  const fetchTripsFromEvents = async () => {
    try {
       const eventsRef = collection(db, 'events');

       // Look for events marked as trips
       const tripEventsQuery = query(eventsRef, where('type', '==', 'trip'));
       const tripEventsSnapshot = await getDocs(tripEventsQuery);

       // If we found some events marked as trips, use them
       if (tripEventsSnapshot.docs.length > 0) {
          const tripsFromEvents = processEventsAsTrips(tripEventsSnapshot.docs);
          setTrips(tripsFromEvents);
       } else {
          // Use placeholder data for testing
          setTrips([
             {
                id: 'trip1',
                tripName: 'Paris Getaway',
                departureDate: new Date().toISOString(),
                returnDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                destination: 'Paris, France',
                tripDescription: 'A wonderful week in the city of lights',
                transportationType: 'Flight',
                cost: 1299,
                currency: 'USD',
                status: 'planned',
             },
             {
                id: 'trip2',
                tripName: 'Tokyo Adventure',
                departureDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                returnDate: new Date(Date.now() + 40 * 24 * 60 * 60 * 1000).toISOString(),
                destination: 'Tokyo, Japan',
                tripDescription: 'Explore the culture and cuisine of Tokyo',
                transportationType: 'Flight',
                cost: 2499,
                currency: 'USD',
                status: 'booking',
             },
          ]);
       }
    } catch (error) {
      console.error('Error fetching trips from events:', error);
      setTrips([]);
    }
  };

  // Process trips data from Firestore
  const processTripsData = (docs: any[]) => {
    return docs.map(doc => {
      const data = doc.data();
      
      // Generate a destination-based image if we have location data
      const destination = data.destination || data.location || '';
      let imageUrl = data.imageUrl || data.coverPhoto || data.photoUrl || data.image || '';
      
      // If no image URL is provided, create one based on destination
      if (!imageUrl && destination) {
        // Use Unsplash for high-quality destination images
        imageUrl = `https://source.unsplash.com/featured/?travel,${encodeURIComponent(destination)}`;
      } else if (!imageUrl) {
        // Default travel images by category if no destination is specified
        const categories = ['beach', 'mountain', 'city', 'countryside', 'landmark'];
        const randomCategory = categories[Math.floor(Math.random() * categories.length)];
        imageUrl = `https://source.unsplash.com/featured/?travel,${randomCategory}`;
      }
      
      // Create a meaningful name if none exists
      let tripName = data.tripName || data.name || '';
      if (!tripName && destination) {
        tripName = `Trip to ${destination}`;
      } else if (!tripName) {
        // Default names if no destination
        const prefixes = ['Adventure', 'Journey', 'Expedition', 'Getaway', 'Vacation'];
        const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        tripName = `${randomPrefix} Trip`;
      }
      
      return {
        id: doc.id,
        tripName,
        departureDate: data.departureDate || data.startDate || new Date().toISOString(),
        returnDate: data.returnDate || data.endDate,
        destination,
        tripDescription: data.tripDescription || data.description,
        transportationType: data.transportationType || data.transportation,
        accommodationDetails: data.accommodationDetails || data.accommodation,
        cost: data.cost || 0,
        currency: data.currency || 'USD',
        maxParticipants: data.maxParticipants || 0,
        status: data.status || 'planned',
        imageUrl,
        // Keep original data for reference
        ...data
      };
    });
  };

  // Process events as trips
  const processEventsAsTrips = (docs: any[]) => {
    return docs.map(doc => {
      const data = doc.data();
      
      // Generate a destination-based image if we have location data
      const destination = data.location || data.venue || '';
      let imageUrl = data.imageUrl || data.coverPhoto || data.photoUrl || data.image || '';
      
      // If no image URL is provided, create one based on destination
      if (!imageUrl && destination) {
        // Use Unsplash for high-quality destination images
        imageUrl = `https://source.unsplash.com/featured/?travel,${encodeURIComponent(destination)}`;
      } else if (!imageUrl) {
        // Default travel images by category if no destination is specified
        const categories = ['beach', 'mountain', 'city', 'countryside', 'landmark'];
        const randomCategory = categories[Math.floor(Math.random() * categories.length)];
        imageUrl = `https://source.unsplash.com/featured/?travel,${randomCategory}`;
      }
      
      // Create a meaningful name if none exists
      let tripName = data.name || data.eventName || '';
      if (!tripName && destination) {
        tripName = `Trip to ${destination}`;
      } else if (!tripName) {
        // Default names if no destination
        const prefixes = ['Adventure', 'Journey', 'Expedition', 'Getaway', 'Vacation'];
        const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        tripName = `${randomPrefix} Trip`;
      }
      
      return {
        id: doc.id,
        tripName,
        departureDate: data.startDate || data.eventDate || new Date().toISOString(),
        returnDate: data.endDate || null,
        destination,
        tripDescription: data.description || data.eventDescription || '',
        transportationType: data.transportationType || 'Not specified',
        cost: data.cost || 0,
        currency: data.currency || 'USD',
        status: 'planned',
        imageUrl,
        // Keep original data for reference
        ...data
      };
    });
  };

  // Fetch selected contacts details
  const fetchSelectedContacts = async () => {
    setLoading(true);
    try {
      const contacts: Contact[] = [];
      
      for (const contactId of contactsToInvite) {
        const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
        const contactSnap = await getDoc(contactRef);
        
        if (contactSnap.exists()) {
          const data = contactSnap.data();
          contacts.push({
            id: contactSnap.id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            company: data.company || '',
            title: data.title || '',
            tags: Array.isArray(data.tags) ? data.tags : [],
          });
        }
      }
      
      setContactsData(contacts);
      setSelectedContactIds(contactsToInvite);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get Contact objects from IDs
  const getSelectedContactsObjects = (): Contact[] => {
    return contactsData.filter(contact => selectedContactIds.includes(contact.id));
  };

  // Handle next step
  const handleNext = () => {
    // Validate current step
    if (activeStep === (isSingleContact ? 0 : 1) && !selectedTrip) {
      // Trip selection step - require trip selection
      return;
    }
    
    if (activeStep === (isSingleContact ? 1 : 2) && (!emailTemplate.subject || !emailTemplate.body)) {
      // Email template step - require subject and body
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle trip selection
  const handleTripSelect = (trip: TripData) => {
    setSelectedTrip(trip);
    // If we already have an email template setup, update it with the new trip info
    if (emailTemplate.subject || emailTemplate.body) {
      updateEmailTemplateWithTrip(trip);
    } else {
      // Pre-populate email template based on selected trip
      setEmailTemplate({
        subject: `Join Our Trip to ${trip.destination || trip.tripName}`,
        body: `
Dear {{contactName}},

We're excited to invite you to join our upcoming trip to ${trip.destination || trip.tripName}!

Trip Details:
- Destination: ${trip.destination || trip.tripName}
- Departure Date: {{departureDate}}
${trip.returnDate ? `- Return Date: ${formatDate(trip.returnDate)}` : ''}
- Transportation: ${trip.transportationType || 'To be determined'}
${trip.accommodationDetails ? `- Accommodation: ${trip.accommodationDetails}` : ''}
${trip.cost ? `- Estimated Cost: ${trip.cost} ${trip.currency}` : ''}

Description:
${trip.tripDescription || 'Join us on this exciting journey!'}

Please let us know if you're interested in participating. We have limited spots available and would love to have you join us!

Best regards,
{{organizationName}} Team
        `,
        includeCalendarInvite: true,
        includeTripDetails: true,
      });
    }
  };

  // Update email template with new trip details
  const updateEmailTemplateWithTrip = (trip: TripData) => {
    setEmailTemplate(prev => ({
      ...prev,
      subject: `Join Our Trip to ${trip.destination || trip.tripName}`,
    }));
  };

  // Handle contact selection
  const handleContactSelect = (contactIds: string[]) => {
    setSelectedContactIds(contactIds);
  };

  // Handle email template changes
  const handleEmailTemplateChange = (template: EmailTemplate) => {
    setEmailTemplate(template);
  };

  // Send trip invitations
  const handleSend = async () => {
    if (!selectedTrip || selectedContactIds.length === 0 || !emailTemplate.subject || !emailTemplate.body) {
      return;
    }
    
    setSendingStatus('sending');
    setSendingProgress(0);
    
    try {
      const selectedContacts = getSelectedContactsObjects();
      const total = selectedContacts.length;
      let completed = 0;
      
      // Simulate sending emails (would be replaced with actual email sending)
      for (const contact of selectedContacts) {
         // Replace placeholders in template
         const subject = emailTemplate.subject
            .replace('{{contactName}}', `${contact.firstName} ${contact.lastName}`.trim())
            .replace('{{departureDate}}', formatDate(selectedTrip.departureDate))
            .replace('{{organizationName}}', 'Your Organization');

         const body = emailTemplate.body
            .replace(/{{contactName}}/g, `${contact.firstName} ${contact.lastName}`.trim())
            .replace(/{{departureDate}}/g, formatDate(selectedTrip.departureDate))
            .replace(/{{organizationName}}/g, 'Your Organization');

         // Wait for a short time to simulate sending process
         await new Promise((resolve) => setTimeout(resolve, 300));

         completed++;
         setSendingProgress(Math.round((completed / total) * 100));
      }
      
      // Update contact records in Firestore (would be implemented based on your data model)
      // This is where you'd update the contacts with the trip invitation status
      
      setSendingStatus('success');
    } catch (error) {
      console.error('Error sending trip invitations:', error);
      setSendingStatus('error');
    }
  };

  // Handle dialog close
  const handleClose = () => {
    // Only allow closing if we're not in the middle of sending
    if (sendingStatus !== 'sending') {
      // Reset state
      setActiveStep(0);
      setSelectedTrip(null);
      setSelectedContactIds([]);
      setEmailTemplate({
        subject: '',
        body: '',
        includeCalendarInvite: true,
        includeTripDetails: true,
      });
      setSendingStatus('idle');
      setSendingProgress(0);
      setIsSingleContact(false);
      
      // Call parent's onClose
      onClose();
    }
  };

  // Render the current step
  const renderStep = () => {
    // If we're in single contact mode, adjust step index
    const stepIndex = isSingleContact ? activeStep : activeStep;
    
    switch (stepIndex) {
      case 0:
        // First step: Contact selection (only in multiple contacts mode)
        if (!isSingleContact) {
          return (
            <ContactListStep
              contacts={contactsData}
              selectedContacts={selectedContactIds}
              onContactsChange={handleContactSelect}
            />
          );
        } else {
          // If single contact, show trip selection instead
          return (
            <TripSelectionStep
              trips={trips}
              selectedTrip={selectedTrip}
              onTripSelect={handleTripSelect}
              loading={loading}
            />
          );
        }
      case 1:
        // Second step: Trip selection (in multiple contacts mode) or Email template (in single contact mode)
        if (!isSingleContact) {
          return (
            <TripSelectionStep
              trips={trips}
              selectedTrip={selectedTrip}
              onTripSelect={handleTripSelect}
              loading={loading}
            />
          );
        } else {
          return (
            <EmailTemplateStep
              template={emailTemplate}
              onChange={handleEmailTemplateChange}
              selectedEvent={selectedTrip ? {
                eventName: selectedTrip.tripName,
                eventDate: selectedTrip.departureDate,
                eventDescription: selectedTrip.tripDescription,
                location: selectedTrip.destination
              } : null}
              selectedContacts={contactsData.filter(contact => contact.id === selectedContactIds[0])}
            />
          );
        }
      case 2:
        // Third step: Email template (in multiple contacts mode) or Review (in single contact mode)
        if (!isSingleContact) {
          return (
            <EmailTemplateStep
              template={emailTemplate}
              onChange={handleEmailTemplateChange}
              selectedEvent={selectedTrip ? {
                eventName: selectedTrip.tripName,
                eventDate: selectedTrip.departureDate,
                eventDescription: selectedTrip.tripDescription,
                location: selectedTrip.destination
              } : null}
              selectedContacts={getSelectedContactsObjects()}
            />
          );
        } else {
          return (
            <ReviewStep
              contacts={getSelectedContactsObjects()}
              trip={selectedTrip}
              emailTemplate={emailTemplate}
              onSend={handleSend}
              sending={sendingStatus === 'sending'}
              sendingProgress={sendingProgress}
            />
          );
        }
      case 3:
        // Fourth step: Review (only in multiple contacts mode)
        return (
          <ReviewStep
            contacts={getSelectedContactsObjects()}
            trip={selectedTrip}
            emailTemplate={emailTemplate}
            onSend={handleSend}
            sending={sendingStatus === 'sending'}
            sendingProgress={sendingProgress}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={sendingStatus === 'sending'}
    >
      <DialogTitle>
        Send Trip Invitation
      </DialogTitle>
      
      <DialogContent>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Loading indicator */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {/* Success/Error messages */}
        {sendingStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Trip invitations sent successfully!
          </Alert>
        )}
        
        {sendingStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            There was an error sending the invitations. Please try again.
          </Alert>
        )}
        
        {/* Step content */}
        {renderStep()}
        
      </DialogContent>
      
      <DialogActions>
        {/* Back button (not on first step or when sending/success) */}
        {activeStep > 0 && sendingStatus === 'idle' && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        
        {/* Next/Finish button (not on last step) */}
        {activeStep < steps.length - 1 && (
          <Button 
            variant="contained" 
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !isSingleContact && selectedContactIds.length === 0) ||
              (activeStep === (isSingleContact ? 0 : 1) && !selectedTrip) ||
              sendingStatus !== 'idle'
            }
          >
            Next
          </Button>
        )}
        
        {/* Close button (only after success or error) */}
        {(sendingStatus === 'success' || sendingStatus === 'error') && (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendTripDialog;
